import { createContext, MutableRefObject, ReactNode, useCallback, useContext, useRef } from 'react';
import { useDispatch } from '@bridebook/web/lib/utils';
import { closeBookingConfirmationModal } from '@bridebook/web/lib/venue-confirmation/actions';

interface VenueConfirmationContextValue {
  onCloseRef: MutableRefObject<(() => void) | null>;
  setOnClose: (callback: (() => void) | null) => void;
  handleClose: () => void;
}

const VenueConfirmationContext = createContext<VenueConfirmationContextValue | null>(null);

export const VenueConfirmationContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const onCloseRef = useRef<(() => void) | null>(null);

  const setOnClose = useCallback((callback: (() => void) | null) => {
    onCloseRef.current = callback;
  }, []);

  const handleClose = useCallback(() => {
    onCloseRef.current?.();
    dispatch(closeBookingConfirmationModal());
  }, [onCloseRef, dispatch]);

  return (
    <VenueConfirmationContext.Provider value={{ onCloseRef, setOnClose, handleClose }}>
      {children}
    </VenueConfirmationContext.Provider>
  );
};

export const useVenueConfirmationContext = () => {
  const context = useContext(VenueConfirmationContext);
  if (!context) {
    throw new Error(
      'useVenueConfirmationContext must be used within VenueConfirmationContextProvider',
    );
  }
  return context;
};
